import { AppConst } from '@/constants'
import { LibraryEntry } from '@/store/modules/teamlibrary/types'
import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'
import type * as THREE from 'three'
import type {
  BuiltModelData,
  ContextResult,
  DownloadedModelFiles,
  GenerateJigContext,
  GenerateJigInput,
  GenerateJigProgress,
  GenerateJigResult,
  LibraryContext,
  MemeGeometryRecoveryPayload,
  MemeMetaFile,
  StageInstructionWithTools,
  TenantContext,
  UseCasePromptFlavoring,
} from './generate-jig/generate-jig.types'
import { MemeGeometryRecoveryService } from './generate-jig/meme-recovery/meme-geometry-recovery-service'

export type { GenerateJigInput, GenerateJigProgress, GenerateJigResult } from './generate-jig/generate-jig.types'

import { generateStageInstructions as generateStageInstructionsFn } from './generate-jig/generate-jig.ai-prompts'
import {
  buildAuthHeaders as buildAuthHeadersFn,
  buildJigApiHeaders as buildJigApiHeadersFn,
  ensureAuthContext,
} from './generate-jig/generate-jig.auth'
import { fetchContext as fetchContextFn } from './generate-jig/generate-jig.context'
import {
  buildModelData as buildModelDataFn,
  buildSceneContextForModelData as buildSceneContextForModelDataFn,
  downloadModelFiles as downloadModelFilesFn,
  loadObjFromBlob as loadObjFromBlobFn,
} from './generate-jig/generate-jig.model'
import { buildModelSceneSource, buildSceneContextFromSource } from './generate-jig/generate-jig.model-scene'
import { applyModelAndStagesToJig as applyModelAndStagesToJigFn } from './generate-jig/generate-jig.stages'
import { tryGenerateAndUploadThumbnail as tryGenerateAndUploadThumbnailFn } from './generate-jig/generate-jig.thumbnail'
import { buildJigName, buildPreviewUrl, extractDeeplink, extractJigMeta, isRequestCanceled } from './generate-jig/generate-jig.utils'
import { matchRecoveredBoundsToParts } from './generate-jig/meme-recovery/meme-geometry-recovery-service'
import type { ConventionPartLike } from './generate-jig/viewpoint/coordinate-convention'
import { classifyViewpointConvention } from './generate-jig/viewpoint/scene-context-normalizer'

declare var consoleError: any
declare var consoleLog: any

export const GENERATE_JIG_COMPLETION_UNKNOWN_WARNING_COPY = 'We couldn\'t confirm Generate Jig completed. Check My Jigs before retrying.'
export const GENERATE_JIG_THUMBNAIL_WARNING_COPY = 'Thumbnail generation failed. You can edit this Jig in Jig app to generate a new thumbnail.'

export class GenerateJigCompletionUnknownError extends Error {
  constructor(cause?: any) {
    super(GENERATE_JIG_COMPLETION_UNKNOWN_WARNING_COPY)
    this.name = 'GenerateJigCompletionUnknownError'
    this.cause = cause
  }
}

export default class GenerateJigService {
  private readonly apiDomain: string
  private readonly templateJigId: string
  private readonly tenantId: number
  private readonly ctx: GenerateJigContext
  private readonly abortController: AbortController
  private copiedJigId: string = ''
  private cancelRequested: boolean = false
  private cancelPromise: Promise<void> | null = null
  private hasAttemptedJigUpdate: boolean = false
  private hasSuccessfulJigUpdate: boolean = false

  constructor(tenantId: number) {
    this.apiDomain = (AppConst.apiDomain || '').replace(/\/$/, '')
    this.templateJigId = ((process.env.VUE_APP_WEBSPARK_TEMPLATE_JIG_ID as string) || '').trim()
    this.tenantId = tenantId
    this.abortController = new AbortController()
    this.ctx = {
      apiDomain: this.apiDomain,
      tenantId: this.tenantId,
      templateJigId: this.templateJigId,
      buildAuthHeaders: (includeAppName: boolean, extra?: Record<string, string>) => buildAuthHeadersFn(this.tenantId, includeAppName, extra),
      buildJigApiHeaders: (includeAppName: boolean, extra?: Record<string, string>) => buildJigApiHeadersFn(this.tenantId, includeAppName, extra),
      getRequestConfig: (config?: AxiosRequestConfig): AxiosRequestConfig => this.getRequestConfig(config),
    }
  }

  public async cancel(): Promise<void> {
    if (this.cancelPromise) {
      return this.cancelPromise
    }

    this.cancelRequested = true

    if (!this.abortController.signal.aborted) {
      this.abortController.abort()
    }

    this.cancelPromise = this.cleanupCopiedJig()
    return this.cancelPromise
  }

  public commitGeneratedJig(): void {
    this.copiedJigId = ''
  }

  public async discardGeneratedJig(): Promise<void> {
    await this.cleanupCopiedJig()
  }

  public async generateJig(input: GenerateJigInput, onProgress?: (progress: GenerateJigProgress) => void): Promise<GenerateJigResult> {
    if (!this.templateJigId) {
      throw new Error('Missing VUE_APP_WEBSPARK_TEMPLATE_JIG_ID')
    }

    this.ensureAuthContext()

    if (!input || !input.libraryEntry || !input.libraryEntry.Item) {
      throw new Error('Invalid library entry payload')
    }

    const displayName = input.libraryEntry.DisplayName || 'Generated Jig'
    const copyName = buildJigName(input.jigName || '', displayName, input.jigUseCase || '')
    const shouldLoadPromptContext = this.shouldLoadPromptContext(input)
    this.hasAttemptedJigUpdate = false
    this.hasSuccessfulJigUpdate = false

    let contextResult: ContextResult | null = null
    let warningCopy: string = ''

    if (shouldLoadPromptContext) {
      this.notify(onProgress, 'Getting things ready...', 4)
      try {
        contextResult = await fetchContextFn(this.ctx, input.libraryEntry, input.libraryModelReferences || [])
      } catch (contextError) {
        if (this.isCancellationError(contextError)) {
          throw contextError
        }
        consoleError('Failed to fetch tenant context (non-fatal): ' + String(contextError))
      }
      await this.ensureNotCanceled()
    }

    try {
      this.notify(onProgress, 'Setting up a new Jig...', 8)
      const { jigId, copyResponse } = await this.copyTemplateJig(copyName)
      this.copiedJigId = jigId
      await this.ensureNotCanceled()

      this.notify(onProgress, 'Downloading your files...', 20)
      const downloadedFiles = await this.downloadModelFiles(input.libraryEntry)
      await this.ensureNotCanceled()

      this.notify(onProgress, 'Processing your model...', 32)
      const object3d = await this.loadObjFromBlob(downloadedFiles.objBlob)
      await this.ensureNotCanceled()

      this.notify(onProgress, 'Thinking for a moment...', 42)
      const copiedJig = await this.fetchJig(jigId)
      await this.ensureNotCanceled()

      this.notify(onProgress, 'Adding some more context...', 54)
      const recoveredBounds = await this.recoverMemeBounds(downloadedFiles, object3d)
      await this.ensureNotCanceled()
      const modelData = this.buildModelData(object3d, downloadedFiles.memeUri, downloadedFiles.memeMeta, input.libraryEntry.Item.FileName, recoveredBounds)
      await this.ensureNotCanceled()

      this.notify(onProgress, 'Building your steps...', 72)
      const stageInstructions = await this.generateStageInstructions(
        jigId,
        input.userQuestion,
        input.libraryEntry.Item.FileName,
        modelData.serializedElements,
        input.customPresentationPrompt || '',
        this.buildUseCasePromptFlavoring(input, contextResult),
        this.getRequestConfig()
      )
      await this.ensureNotCanceled()

      const shouldPlanViewpoint = this.hasViewpointInstructions(stageInstructions)

      if (shouldPlanViewpoint) {
        this.notify(onProgress, 'Setting up the views...', 79)
      }

      const hydratedModelData = shouldPlanViewpoint ? this.buildSceneContext(modelData) : modelData

      this.notify(onProgress, 'Saving your Jig...', 86)
      const updatedJig = this.applyModelAndStagesToJig(copiedJig, hydratedModelData, stageInstructions, input.userQuestion)
      this.hasAttemptedJigUpdate = true
      const putResponse = await this.updateJig(jigId, updatedJig)
      this.hasSuccessfulJigUpdate = true
      await this.ensureNotCanceled()

      const putMeta = extractJigMeta(putResponse) || extractJigMeta(copyResponse) || {}
      const jigVersion = String(putMeta.Version || '')
      const thumbnailUrl = String(putMeta.ThumbnailURL || putMeta.thumbnailURL || '')

      this.notify(onProgress, 'Creating a thumbnail...', 94)
      try {
        await this.tryGenerateAndUploadThumbnail(
          jigId,
          input.userQuestion,
          input.libraryEntry.DisplayName,
          input.libraryEntry.ThumbnailUrl,
          jigVersion,
          thumbnailUrl
        )
      } catch (thumbnailError) {
        if (this.isCancellationError(thumbnailError)) {
          throw thumbnailError
        }
        warningCopy = GENERATE_JIG_THUMBNAIL_WARNING_COPY
        consoleError('Generate Jig thumbnail warning: ' + String(thumbnailError))
      }
      await this.ensureNotCanceled()

      this.notify(onProgress, 'Almost there...', 98)
      const deeplink = extractDeeplink(copyResponse)
      const previewUrl = buildPreviewUrl(this.apiDomain, copyResponse, deeplink)
      this.notify(onProgress, 'Jig created successfully.', 100)

      return {
        jigId,
        deeplinkURL: deeplink,
        editURL: deeplink,
        previewURL: previewUrl,
        warningCopy: warningCopy,
      }
    } catch (error) {
      if (this.isCancellationError(error)) {
        throw error
      }

      // Failure handling matrix:
      // 1. Before updateJig() is attempted, any ordinary failure means the copied template Jig
      //    is definitely orphaned, so clean it up automatically.
      // 2. If updateJig() returns a definite HTTP 4xx/5xx before we have confirmed success,
      //    treat that as a real failure and clean up the copied Jig for a tidy retry.
      // 3. Once updateJig() has succeeded, the Jig may already be durable. From that point on,
      //    ordinary failures (for example transport uncertainty or thumbnail generation issues)
      //    must not auto-delete the Jig because that could destroy successful output.
      // 4. Explicit user cancel/close before preview is handled separately by the modal/service
      //    cancel-discard path and should still delete the copied Jig.
      if (!this.hasAttemptedJigUpdate) {
        await this.cleanupCopiedJig()
        throw error
      }

      if (!this.hasSuccessfulJigUpdate && this.isDefinitePostPersistFailure(error)) {
        await this.cleanupCopiedJig()
        throw error
      }

      if (this.isDefinitePostPersistFailure(error)) {
        throw error
      }

      consoleError('Generate Jig completion could not be confirmed: ' + String(error))
      throw new GenerateJigCompletionUnknownError(error)
    }
  }

  private notify(onProgress: ((progress: GenerateJigProgress) => void) | undefined, message: string, percentage: number): void {
    if (onProgress) {
      onProgress({
        message: message,
        percentage: percentage,
      })
    }
  }

  private ensureAuthContext(): void {
    ensureAuthContext(this.tenantId)
  }

  private getRequestConfig(config?: AxiosRequestConfig): AxiosRequestConfig {
    const nextConfig = config ? { ...config } : {}
    return {
      ...nextConfig,
      signal: this.abortController.signal,
    }
  }

  private isCancellationError(error: any): boolean {
    return this.cancelRequested || isRequestCanceled(error)
  }

  private isDefinitePostPersistFailure(error: any): boolean {
    return axios.isAxiosError(error) && error.response != null && typeof error.response.status === 'number'
  }

  private async ensureNotCanceled(): Promise<void> {
    if (!this.cancelRequested && !this.abortController.signal.aborted) {
      return
    }

    await this.cleanupCopiedJig()
    throw new Error('Generate Jig request was cancelled')
  }

  private async cleanupCopiedJig(): Promise<void> {
    if (!this.copiedJigId) {
      return
    }

    const jigId = this.copiedJigId
    this.copiedJigId = ''

    try {
      await axios.delete(this.apiDomain + '/v2/me/jigs/' + jigId, {
        headers: this.buildJigApiHeaders(true),
      })
    } catch (error) {
      consoleError('Failed to delete cancelled copied Jig ' + jigId + ': ' + String(error))
    }
  }

  private shouldLoadPromptContext(input: GenerateJigInput): boolean {
    return !!(
      input &&
      typeof input.jigUseCaseTemplateName === 'string' &&
      input.jigUseCaseTemplateName.trim().length > 0
    )
  }

  private hasViewpointInstructions(stageInstructions: StageInstructionWithTools[]): boolean {
    if (!Array.isArray(stageInstructions) || stageInstructions.length === 0) {
      return false
    }

    return stageInstructions.some(function (stageInstruction: StageInstructionWithTools) {
      return !!(stageInstruction && Array.isArray(stageInstruction.viewpoint) && stageInstruction.viewpoint.length > 0)
    })
  }

  private buildUseCasePromptFlavoring(
    input: GenerateJigInput,
    contextResult: ContextResult | null
  ): UseCasePromptFlavoring | null {
    const templateName = input.jigUseCaseTemplateName ? input.jigUseCaseTemplateName.trim() : ''
    if (!templateName) {
      return null
    }

    const tenant = contextResult ? contextResult.tenant : null
    const library = contextResult ? contextResult.library : null
    const tenantWebsiteSummary = this.getFirstNonEmptySummary(tenant ? tenant.websiteSummaries : [])
    const tenantName = tenant && tenant.tenantName
      ? tenant.tenantName
      : (input.tenantName || '')

    return {
      templateName: templateName,
      renderVariables: {
        TenantName: tenantName,
        TenantWebURLContextSummary: tenantWebsiteSummary,
        Context: {
          TenantInstruction: {
            Verbatim: tenant && tenant.instructionsVerbatim ? tenant.instructionsVerbatim : '',
          },
        },
        LibraryItemDisplayName: input.libraryEntry.DisplayName || '',
        LibraryItemDescription: input.libraryEntry.Description || '',
        libraryItemFilename: input.libraryEntry.Item && input.libraryEntry.Item.FileName
          ? input.libraryEntry.Item.FileName
          : '',
        LibraryItemURLReferenceSummary: library && library.entryWebsiteSummary
          ? library.entryWebsiteSummary
          : '',
        LibraryItemPDFReferenceSummary: this.joinModelReferenceSummaries(library),
        KeepExactElementName: 'true',
      },
    }
  }

  private joinModelReferenceSummaries(library: LibraryContext | null): string {
    if (!library || !library.modelReferenceContexts || library.modelReferenceContexts.length === 0) {
      return ''
    }
    const parts: string[] = []
    for (let i = 0; i < library.modelReferenceContexts.length; i++) {
      const ref = library.modelReferenceContexts[i]
      if (ref && ref.summary) {
        parts.push(ref.summary)
      }
    }
    return parts.join('\n')
  }

  private getFirstNonEmptySummary(
    entries: Array<{ summary: string | null }> | null | undefined
  ): string {
    if (!entries || entries.length === 0) {
      return ''
    }

    for (let i = 0; i < entries.length; i++) {
      const summary = entries[i] && entries[i].summary ? String(entries[i].summary).trim() : ''
      if (summary.length > 0) {
        return summary
      }
    }

    return ''
  }

  private buildAuthHeaders(includeAppName: boolean, extraHeaders: Record<string, string> = {}): Record<string, string> {
    return buildAuthHeadersFn(this.tenantId, includeAppName, extraHeaders)
  }

  private buildJigApiHeaders(includeAppName: boolean, extraHeaders: Record<string, string> = {}): Record<string, string> {
    return buildJigApiHeadersFn(this.tenantId, includeAppName, extraHeaders)
  }

  private async copyTemplateJig(copyName: string): Promise<{ jigId: string; copyResponse: any }> {
    const url = this.apiDomain + '/me/jigs/v2/' + this.templateJigId
    const response = await axios.post(
      url,
      null,
      this.getRequestConfig({
        params: {
          action: 'copy',
          name: copyName,
        },
        headers: this.buildJigApiHeaders(true),
      })
    )

    const responseData = response && response.data ? response.data : {}
    const jigId = String(responseData.id || responseData.Id || responseData.jigId || '')

    if (!jigId) {
      throw new Error('Failed to create Jig copy (missing ID)')
    }

    return { jigId, copyResponse: responseData }
  }

  private async downloadModelFiles(libraryEntry: LibraryEntry): Promise<DownloadedModelFiles> {
    return downloadModelFilesFn(this.ctx, libraryEntry)
  }

  private async loadObjFromBlob(blob: Blob): Promise<THREE.Object3D> {
    return loadObjFromBlobFn(blob)
  }

  private buildModelData(
    modelObject: THREE.Object3D,
    modelUri: string,
    memeMetaData: MemeMetaFile | null,
    modelFileName: string,
    recoveredBounds: MemeGeometryRecoveryPayload | null
  ): BuiltModelData {
    return buildModelDataFn(this.apiDomain, modelObject, modelUri, memeMetaData, modelFileName, {
      recoveredBounds: recoveredBounds,
    })
  }

  private getRecoveryCandidateParts(modelObject: THREE.Object3D, memeMetaData: MemeMetaFile | null): ConventionPartLike[] | null {
    modelObject.updateMatrixWorld(true)
    const sceneSource = buildModelSceneSource(modelObject, '__RecoveryInnerParent')
    const sceneContext = buildSceneContextFromSource(sceneSource)
    if (!sceneContext || !sceneContext.elements || sceneContext.elements.length === 0) {
      consoleLog('[recovery] candidate parts: skip — empty scene context')
      return null
    }

    const classification = classifyViewpointConvention(sceneContext, memeMetaData)
    consoleLog(
      '[recovery] detection:'
      + ' canCanonicalize=' + classification.detection.canCanonicalize
      + ' mode=' + classification.mode
      + ' resolution=' + classification.detection.resolution
      + ' matched=' + classification.detection.matchedPartCount
      + '/' + sceneContext.elements.length
      + ' xCorr=' + (classification.detection.xCorrelation === null ? 'n/a' : classification.detection.xCorrelation.toFixed(2))
      + ' zCorr=' + (classification.detection.zCorrelation === null ? 'n/a' : classification.detection.zCorrelation.toFixed(2))
      + ' memeMeta=' + (memeMetaData ? 'present' : 'missing')
    )

    return sceneContext.elements.map(function (element) {
      const displayAlias = element.aliases.find(function (alias) {
        return alias && alias !== element.id
      })

      return {
        name: displayAlias || element.id,
        objectName: element.id,
        center: element.bounds.center,
        size: element.bounds.size,
      }
    })
  }

  private async recoverMemeBounds(downloadedFiles: DownloadedModelFiles, modelObject: THREE.Object3D): Promise<MemeGeometryRecoveryPayload | null> {
    consoleLog(
      '[recovery] enter: memeUri=' + (downloadedFiles.memeUri || '(none)')
      + ' memeBuffer=' + (downloadedFiles.memeBuffer ? downloadedFiles.memeBuffer.byteLength + 'B' : 'null')
      + ' memeMeta=' + (downloadedFiles.memeMeta ? 'present' : 'null')
    )

    if (!downloadedFiles.memeBuffer || !/\.meme(?:$|[?#])/i.test(downloadedFiles.memeUri || '')) {
      consoleLog('[recovery] skip — non-meme URI or no buffer')
      return null
    }

    const candidateParts = this.getRecoveryCandidateParts(modelObject, downloadedFiles.memeMeta)
    if (!candidateParts || candidateParts.length === 0) {
      consoleLog('[recovery] skip — no candidate parts for recovery')
      return null
    }
    consoleLog('[recovery] candidate parts: ' + candidateParts.length)

    const service = new MemeGeometryRecoveryService({
      fetchBufferFn: async (uri: string) => (uri === downloadedFiles.memeUri ? downloadedFiles.memeBuffer : null),
    })

    try {
      const recovered = await service.recoverBoundsForModelUri(downloadedFiles.memeUri)
      if (!recovered) {
        consoleLog('[recovery] decode returned null — recovery unavailable')
        return null
      }
      consoleLog('[recovery] decoded sub-objects: ' + recovered.subObjects.length)

      const matchedRecoveredBounds = matchRecoveredBoundsToParts(candidateParts, recovered.subObjects)
      if (!matchedRecoveredBounds || matchedRecoveredBounds.length !== candidateParts.length) {
        consoleLog(
          '[recovery] FAIL — match incomplete: matched='
          + (matchedRecoveredBounds ? matchedRecoveredBounds.length : 0)
          + '/' + candidateParts.length
          + ' (falls back to raw setFromObject bounds)'
        )
        return null
      }

      const samples = matchedRecoveredBounds.slice(0, 3).map(function (match) {
        const c = match.viewerCenter
        return match.referenceMeshName + '=(' + c.x.toFixed(3) + ',' + c.y.toFixed(3) + ',' + c.z.toFixed(3) + ')'
      }).join(' ')
      consoleLog(
        '[recovery] SUCCESS — matched ' + matchedRecoveredBounds.length + '/' + candidateParts.length
        + ' sample viewerCenters: ' + samples
      )

      return {
        source: recovered.source,
        modelUri: recovered.modelUri,
        subObjects: recovered.subObjects.map(function (subObject) {
          return {
            name: subObject.name,
            materialName: subObject.materialName,
            center: subObject.center,
            size: subObject.size,
          }
        }),
      }
    } catch (error) {
      consoleError('[recovery] threw (non-fatal): ' + String(error))
      return null
    }
  }

  private buildSceneContext(modelData: BuiltModelData): BuiltModelData {
    return buildSceneContextForModelDataFn(modelData)
  }

  private async generateStageInstructions(
    jigId: string,
    userQuestion: string,
    modelFileName: string,
    serializedElements: any[],
    customPresentationPrompt: string,
    promptFlavoring: UseCasePromptFlavoring | null,
    requestConfig?: AxiosRequestConfig
  ): Promise<StageInstructionWithTools[]> {
    return generateStageInstructionsFn(
      this.apiDomain,
      this.tenantId,
      jigId,
      userQuestion,
      modelFileName,
      serializedElements,
      customPresentationPrompt,
      promptFlavoring,
      requestConfig
    )
  }

  private applyModelAndStagesToJig(
    baseJig: any,
    modelData: BuiltModelData,
    stageInstructions: StageInstructionWithTools[],
    userQuestion: string
  ): any {
    return applyModelAndStagesToJigFn(baseJig, modelData, stageInstructions, userQuestion)
  }

  private async fetchJig(jigId: string): Promise<any> {
    const response = await axios.get(this.apiDomain + '/v2/me/jigs/' + jigId, this.getRequestConfig({
      headers: this.buildJigApiHeaders(false),
    }))

    return response.data
  }

  private async updateJig(jigId: string, jigPayload: any): Promise<any> {
    const response = await axios.put(this.apiDomain + '/v2/me/jigs/' + jigId, jigPayload, this.getRequestConfig({
      headers: this.buildJigApiHeaders(true),
    }))
    return response && response.data ? response.data : {}
  }

  private async tryGenerateAndUploadThumbnail(
    jigId: string,
    userQuestion: string,
    displayName: string,
    libraryThumbnailUrl: string,
    jigVersion: string,
    thumbnailUrl: string
  ): Promise<void> {
    return tryGenerateAndUploadThumbnailFn(
      {
        apiDomain: this.apiDomain,
        buildAuthHeaders: this.buildAuthHeaders.bind(this),
        buildJigApiHeaders: this.buildJigApiHeaders.bind(this),
        getRequestConfig: this.getRequestConfig.bind(this),
      },
      jigId,
      userQuestion,
      displayName,
      libraryThumbnailUrl,
      jigVersion,
      thumbnailUrl
    )
  }
}
